import React, { useEffect, useMemo, useRef, useState } from "react";
import { PitchLayout } from "../components/PitchLayout";
import SelectBox from "./components/SelectBox";
import ShotsLayerView from "./components/ShotsLayerView";
import StatsPanel from "./components/StatsPanel";
import MinuteSlider from "./components/MinuteSlider";
import { extractShots } from "./helper";
import { XGShot, circleRadius, summarize } from "./utils";

type TooltipState = {
  shot: XGShot;
  left: number;
  top: number;
  placement: "above" | "below" | "left" | "right";
};

function XgMap({ status }: { status: any }) {
  const { home, away } = extractShots(status);
  const [filterOutcome, setFilterOutcome] = useState<string>("All");
  const [pattern, setPattern] = useState<string>("All");
  const [timeCut, setTimeCut] = useState<number>(95);

  const liveMinute = useMemo(() => Number(status?.tm) || 0, [status?.tm]);
  const endMinute = useMemo(() => {
    const taNum = Number(status?.ta);
    return 90 + (Number.isFinite(taNum) && taNum > 0 ? taNum : 0);
  }, [status?.ta]);

  const filt = useMemo(
    () => (s: XGShot) => {
      if (s.minute != null && s.minute > timeCut) return false;
      if (filterOutcome !== "All") {
        const o = (s.outcome || "").toLowerCase();
        if (filterOutcome === "Goal" && o !== "goal") return false;
        if (filterOutcome === "On Target" && !o.includes("on")) return false;
        if (filterOutcome === "Off Target" && !o.includes("off")) return false;
      }
      if (pattern !== "All") {
        // set-piece / open-play / penalty
      }
      return true;
    },
    [timeCut, filterOutcome, pattern]
  );

  const sumH = summarize(home.filter(filt));
  const sumA = summarize(away.filter(filt));

  const layerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  const CARD_W = 180;
  const CARD_H = 160;
  const PADDING = 8;

  const computeSafePos = (s: XGShot): TooltipState | null => {
    const host = layerRef.current;
    if (!host) return null;
    const rect = host.getBoundingClientRect();

    const pxX = s.x * rect.width;
    const pxY = s.y * rect.height;

    const r = circleRadius(s.xg || 0);
    const gapH = Math.max(6, Math.min(16, r * 0.6));
    const gapV = Math.max(6, Math.min(16, r * 0.6));

    const placements: ("above" | "right" | "left" | "below")[] = [
      "above",
      "right",
      "left",
      "below",
    ];

    const fits = (pl: "above" | "right" | "left" | "below") => {
      let left = 0,
        top = 0;
      if (pl === "above") {
        left = pxX - CARD_W / 2;
        top = pxY - gapV - CARD_H;
      } else if (pl === "below") {
        left = pxX - CARD_W / 2;
        top = pxY + gapV;
      } else if (pl === "right") {
        left = pxX + gapH;
        top = pxY - CARD_H / 2;
      } else {
        left = pxX - gapH - CARD_W;
        top = pxY - CARD_H / 2;
      }
      const overflowLeft = Math.max(0, PADDING - left);
      const overflowTop = Math.max(0, PADDING - top);
      const overflowRight = Math.max(0, left + CARD_W - (rect.width - PADDING));
      const overflowBottom = Math.max(
        0,
        top + CARD_H - (rect.height - PADDING)
      );
      const totalOverflow =
        overflowLeft + overflowTop + overflowRight + overflowBottom;
      return { left, top, totalOverflow };
    };

    for (const pl of placements) {
      const { left, top, totalOverflow } = fits(pl);
      if (totalOverflow === 0) return { shot: s, left, top, placement: pl };
    }

    let best = {
      pl: placements[0],
      score: Number.POSITIVE_INFINITY,
      left: 0,
      top: 0,
    };
    for (const pl of placements) {
      const { left, top, totalOverflow } = fits(pl);
      if (totalOverflow < best.score)
        best = { pl, score: totalOverflow, left, top };
    }
    return {
      shot: s,
      left: Math.max(
        PADDING,
        Math.min(best.left, layerRef.current!.clientWidth - PADDING - CARD_W)
      ),
      top: Math.max(
        PADDING,
        Math.min(best.top, layerRef.current!.clientHeight - PADDING - CARD_H)
      ),
      placement: best.pl,
    };
  };

  const openShotCard = (s: XGShot) => {
    const pos = computeSafePos(s);
    if (pos) setTooltip(pos);
  };

  useEffect(() => {
    if (!tooltip) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setTooltip(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tooltip]);

  useEffect(() => {
    if (!tooltip) return;
    const onResize = () => {
      const pos = computeSafePos(tooltip.shot);
      if (pos) setTooltip(pos);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [tooltip]);

  return (
    <div className="w-full overflow-x-hidden text-white">
      <StatsPanel sumH={sumH} sumA={sumA} status={status} />

      <div className="flex flex-row gap-[1vw] mb-[1vw]">
        <div className="flex w-full items-center rounded-[0.5vw] border border-border bg-darkestBlue">
          <SelectBox
            label="Outcome"
            value={filterOutcome}
            onChange={(val) => setFilterOutcome(String(val))}
          >
            <option>All</option>
            <option>Goal</option>
            <option>On Target</option>
            <option>Off Target</option>
          </SelectBox>
        </div>

        <div className="flex w-full items-center rounded-[0.5vw] border border-border bg-darkestBlue">
          <SelectBox
            label="Pattern of Play"
            value={pattern}
            onChange={(val) => setPattern(String(val))}
          >
            <option>All</option>
            <option>Open Play</option>
            <option>Set Piece</option>
            <option>Penalty</option>
          </SelectBox>
        </div>
      </div>

      <PitchLayout heightVw={50}>
        <ShotsLayerView
          home={home}
          away={away}
          filt={filt}
          layerRef={layerRef}
          tooltip={tooltip}
          setTooltip={setTooltip}
          openShotCard={openShotCard}
          status={status}
        />
      </PitchLayout>

      <MinuteSlider
        liveMinute={liveMinute}
        endMinute={endMinute}
        value={timeCut}
        onChange={(v) => setTimeCut(v)}
        labels={{ left: "0:00", mid: "FH", right: `${endMinute}:00` }}
      />
    </div>
  );
}

export default XgMap;
